// Preloader utility to prevent stuck loading states
export class PreloaderManager {
  constructor() {
    this.maxLoadTime = 10000; // 10 seconds maximum load time
    this.timeoutId = null;
    this.isHidden = false;
  }

  // Initialize preloader with automatic timeout
  init() {
    // Set up the maximum timeout as a safety net
    this.timeoutId = setTimeout(() => {
      console.warn('Preloader timeout reached - forcing hide');
      this.hide();
    }, this.maxLoadTime);

    // Also hide on window load as a backup
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.hide();
      }, 500); // Small delay to ensure content is rendered
    });

    // Hide on any unhandled errors
    window.addEventListener('error', (event) => {
      console.error('Global error detected:', event.error);
      this.hide();
    });

    // Hide on unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      this.hide();
    });
  }

  // Hide the preloader
  hide() {
    if (this.isHidden) return; // Prevent multiple calls
    
    try {
      const loader = document.querySelector('.loader');
      if (loader) {
        loader.classList.add('hidden');
        // Also set display none after animation
        setTimeout(() => {
          if (loader) {
            loader.style.display = 'none';
          }
        }, 300);
      }
      
      this.isHidden = true;
      
      // Clear the timeout if it exists
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
    } catch (error) {
      console.error('Error hiding preloader:', error);
      // Force hide using direct DOM manipulation as last resort
      try {
        document.querySelectorAll('.loader').forEach(loader => {
          loader.style.display = 'none';
        });
      } catch (e) {
        // Silently fail - preloader issues shouldn't break the page
      }
    }
  }

  // Show the preloader (if needed for subsequent loads)
  show() {
    try {
      const loader = document.querySelector('.loader');
      if (loader) {
        loader.style.display = '';
        loader.classList.remove('hidden');
      }
      this.isHidden = false;
    } catch (error) {
      console.error('Error showing preloader:', error);
    }
  }
}

// Create a singleton instance
export const preloader = new PreloaderManager();

// Helper function for async operations with preloader management
export async function withPreloader(asyncFunction, options = {}) {
  const timeout = options.timeout || 8000; // 8 seconds default for API calls
  
  try {
    // Create a timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Operation timeout')), timeout);
    });
    
    // Race between the actual operation and timeout
    const result = await Promise.race([
      asyncFunction(),
      timeoutPromise
    ]);
    
    return result;
  } catch (error) {
    console.error('Operation failed:', error);
    throw error;
  } finally {
    // Always hide preloader after operation
    preloader.hide();
  }
}